/**
 * 1.8.5 (D-1.8.5-7) — the Tech Wizard's library: what it answers from, and cites.
 *
 * The library is built from data/tech-library/ (the catalog sources.md, checked 10 Oct 2026, is its only source of
 * links): one Markdown entry a topic (entries/*.md, a small front-matter block and "## Steps"), the guided flows
 * (flows/*.json, Walk me through it and "I already tried that"), the free-help lines (free-help.json, catalog §12),
 * Scam Watch's fixed lists (scam-watch.json, §11) and the makers' "What's New" pages (whats-new.json).
 * scripts/gen-tech-library.ts parses all of it with the functions below and writes shared/tech-library.gen.ts.
 *
 * Pure: no Node APIs, shared by the app and the API. Nothing here calls a model — it is the part of the Wizard that
 * still works when the AI is paused (§10).
 *
 * "Checked by [name] on [date]" is not in the entry files: the app keeps it at runtime in
 * _app/tech-library/checks.json and passes it to staleEntries() / staleFlows().
 */
import { HELP_KEYS, type HelpKey } from './tech';

/** Every maker an entry may name — the makers of sources.md, nobody else. */
export const PLATFORMS = [
  'Apple',
  'Google',
  'Samsung',
  'Amazon',
  'Microsoft',
  'Vispero',
  'NV Access',
  'Dell',
  'HP',
  'Lenovo',
  'Epson',
  'Brother',
  'Canon',
  'NETGEAR',
  'Spectrum',
  'Verizon',
  'NLS',
  'HumanWare',
  'APH',
  'Index',
  'ViewPlus',
  'Duxbury',
  'Freedom Scientific',
  'SELVAS BLV',
  'Orbit Research',
  'Be My Eyes',
  'Aira',
  'Roku',
  'NYPL',
  'Eschenbach',
  'OrCam',
  'Envision',
  /** 1.8.5 §7: the fix log — "How we fixed it at Selis Manor", made at runtime from approved after-visit summaries (never a library file). */
  'Selis Manor',
] as const;
export type Platform = (typeof PLATFORMS)[number];

export const CONFIDENCE = ['high', 'medium', 'low'] as const;
export type Confidence = (typeof CONFIDENCE)[number];

/** A step that erases settings or data starts with this, and comes last. */
export const ERASE_MARK = '⚠ This erases';

export interface LibrarySource {
  title: string;
  url: string;
}

export interface LibraryEntry {
  id: string;
  title: string;
  platform: Platform;
  devices: HelpKey[];
  /** The version the steps cover, e.g. "iOS/iPadOS 27". */
  version: string;
  topics: string[];
  sources: LibrarySource[];
  /** The catalog date the entry was written from (yyyy-MM-dd). */
  catalogDate: string;
  /** True when any step erases settings or data. */
  erases: boolean;
  confidence: Confidence;
  /** The Markdown after the front matter: "## Steps" (numbered), optionally "## Good to know". */
  body: string;
}

export interface FlowStep {
  id: string;
  /** One short instruction. */
  text: string;
  /** "Did that work?" wording, when the default isn't right. */
  check?: string;
  erases: boolean;
  /** Required when erases: "This erases saved Wi-Fi passwords". */
  warning?: string;
  /** The library entry the step comes from. */
  entry: string;
  sourceUrl?: string;
  quote?: string;
  sectionId?: string;
}

export interface FreeLine {
  name: string;
  phone: string;
  hours: string;
}

export interface FlowEnd {
  maker: Platform;
  supportUrl: string;
  freeLine?: FreeLine;
}

export interface GuidedFlow {
  id: string;
  title: string;
  devices: HelpKey[];
  /** Words a description might use. */
  match: string[];
  firstQuestion?: string;
  steps: FlowStep[];
  end: FlowEnd;
  version: string;
  confidence: Confidence;
  decisionGuideId?: string;
}

export const FREE_HELP_KINDS = ['maker', 'government', 'nonprofit', 'app'] as const;
export type FreeHelpKind = (typeof FREE_HELP_KINDS)[number];

export interface FreeHelpLine {
  id: string;
  who: string;
  phones: string[];
  email?: string;
  url: string;
  hours?: string;
  note?: string;
  kind: FreeHelpKind;
  /** Only where the catalog says nonprofit. */
  nonprofit: boolean;
  /** The maker this line is for, when it is a maker's line. */
  maker?: Platform;
  /** What it helps with: help keys, or "all" (Be My Eyes, Aira, the elder-fraud lines). */
  helps: HelpKey[] | 'all';
  /** Scam and fraud lines. */
  scam?: boolean;
}

export interface ScamFeed {
  id: string;
  name: string;
  url: string;
  language: 'en' | 'es';
  who: 'government' | 'maker';
  /** How the feed is filtered: everything scam-related, business-only items left out, or scam items only. */
  filter: 'scams-only' | 'drop-business-only' | 'scams-and-fraud';
  note: string;
}

export interface ScamSpotPage {
  id: string;
  title: string;
  url: string;
  who: 'government' | 'maker' | 'provider';
  note?: string;
}

export interface ScamContact {
  id: string;
  kind: 'call' | 'report';
  name: string;
  phone?: string;
  email?: string;
  hours?: string;
  how: string;
  url: string;
  nonprofit: boolean;
  /** False when the catalog lists the page but not this exact number/address — a person confirms it on the page. */
  inCatalog: boolean;
  confirm?: string;
}

export interface ScamWatch {
  catalogDate: string;
  feeds: ScamFeed[];
  noFeed: { who: string; note: string }[];
  spotIt: ScamSpotPage[];
  callOrReport: ScamContact[];
}

export interface WhatsNewPage {
  id: string;
  product: string;
  maker: Platform;
  current: string;
  url: string;
  loadsByScript: boolean;
  note?: string;
}

export interface LibraryGap {
  id: string;
  what: string;
  why: string;
  who: string;
}

export interface WhatsNew {
  catalogDate: string;
  pages: WhatsNewPage[];
  gaps: LibraryGap[];
}

export interface FreeHelp {
  catalogDate: string;
  catalogVersion: string;
  lines: FreeHelpLine[];
}

export interface TechLibrary {
  catalogDate: string;
  entries: LibraryEntry[];
  flows: GuidedFlow[];
  freeHelp: FreeHelp;
  scamWatch: ScamWatch;
  whatsNew: WhatsNew;
}

/** "Checked by [name] on [date]", kept by the app in _app/tech-library/checks.json. */
export interface LibraryCheck {
  by: string;
  /** yyyy-MM-dd or an ISO timestamp (the civil date is its first ten characters). */
  at: string;
}

// ---------------------------------------------------------------------------------------------------------------
// Parsing — strict: a bad entry stops the build with the file and the field.
// ---------------------------------------------------------------------------------------------------------------

const ENTRY_KEYS = ['id', 'title', 'platform', 'devices', 'version', 'topics', 'sources', 'catalogDate', 'erases', 'confidence'] as const;
type EntryKey = (typeof ENTRY_KEYS)[number];
const LIST_KEYS: readonly EntryKey[] = ['devices', 'topics', 'sources'];
const ID_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const BODY_HEADINGS = ['## Steps', '## Good to know'] as const;

function fail(file: string, field: string, message: string): never {
  throw new Error(`${file}: ${field}: ${message}`);
}

const isHelpKey = (s: string): s is HelpKey => (HELP_KEYS as readonly string[]).includes(s);
const isPlatform = (s: string): s is Platform => (PLATFORMS as readonly string[]).includes(s);
const isConfidence = (s: string): s is Confidence => (CONFIDENCE as readonly string[]).includes(s);

/** "Title | https://…" → a source; the url must be https. */
export function parseSourceLine(line: string, file: string): LibrarySource {
  const parts = line.split(' | ');
  if (parts.length !== 2) fail(file, 'sources', `"${line}" is not "title | https://url"`);
  const title = parts[0]!.trim();
  const url = parts[1]!.trim();
  if (!title) fail(file, 'sources', `"${line}" has no title`);
  if (!/^https:\/\/\S+$/.test(url)) fail(file, 'sources', `"${url}" is not an https link`);
  return { title, url };
}

/** The numbered steps of an entry's "## Steps" section, in order. */
export function entrySteps(body: string): string[] {
  const out: string[] = [];
  let inSteps = false;
  for (const line of body.split('\n')) {
    if (line.startsWith('## ')) {
      inSteps = line.trim() === '## Steps';
      continue;
    }
    const m = inSteps ? /^\d+\. (.+)$/.exec(line) : null;
    if (m) out.push(m[1]!);
  }
  return out;
}

const isEraseStep = (s: string) => s.startsWith(ERASE_MARK);

/**
 * Parse one entry file. The front matter is a `---` block of `key: value` lines; `devices`, `topics` and `sources`
 * are lists written as `key:` followed by `  - item` lines. Every key is required, no other key is allowed.
 */
export function parseEntry(md: string, file = '<entry>'): LibraryEntry {
  const text = md.replace(/\r\n/g, '\n');
  if (!text.startsWith('---\n')) fail(file, 'front matter', 'must start with a line "---"');
  const close = text.indexOf('\n---\n', 3);
  if (close < 0) fail(file, 'front matter', 'has no closing "---" line');
  const head = text.slice(4, close).split('\n');
  const body = text.slice(close + 5).replace(/^\n+/, '').replace(/\s+$/, '');

  const scalars = new Map<string, string>();
  const lists = new Map<string, string[]>();
  let current: string | null = null;
  for (const raw of head) {
    if (!raw.trim()) fail(file, 'front matter', 'has a blank line');
    const item = /^ {2}- (.+)$/.exec(raw);
    if (item) {
      if (!current) fail(file, 'front matter', `"${raw}" is a list item under no list`);
      lists.get(current)!.push(item[1]!.trim());
      continue;
    }
    const kv = /^([A-Za-z]+):(?: (.*))?$/.exec(raw);
    if (!kv) fail(file, 'front matter', `cannot read the line "${raw}"`);
    const key = kv[1]!;
    const value = (kv[2] ?? '').trim();
    if (!(ENTRY_KEYS as readonly string[]).includes(key)) fail(file, key, 'is not a known field');
    if (scalars.has(key) || lists.has(key)) fail(file, key, 'appears twice');
    if (LIST_KEYS.includes(key as EntryKey)) {
      if (value) fail(file, key, 'is a list: write "key:" then "  - item" lines');
      lists.set(key, []);
      current = key;
    } else {
      if (!value) fail(file, key, 'is empty');
      scalars.set(key, value);
      current = null;
    }
  }
  for (const k of ENTRY_KEYS) {
    if (LIST_KEYS.includes(k)) {
      if (!lists.has(k)) fail(file, k, 'is missing');
      if (lists.get(k)!.length === 0) fail(file, k, 'has no items');
    } else if (!scalars.has(k)) fail(file, k, 'is missing');
  }

  const id = scalars.get('id')!;
  if (!ID_RE.test(id)) fail(file, 'id', `"${id}" must be lower-case words joined by hyphens`);
  const platform = scalars.get('platform')!;
  if (!isPlatform(platform)) fail(file, 'platform', `"${platform}" is not a maker in the catalog`);
  const devices = lists.get('devices')!;
  for (const d of devices) if (!isHelpKey(d)) fail(file, 'devices', `"${d}" is not a help key (${HELP_KEYS.join(', ')})`);
  if (new Set(devices).size !== devices.length) fail(file, 'devices', 'repeats a device');
  const catalogDate = scalars.get('catalogDate')!;
  if (!DATE_RE.test(catalogDate)) fail(file, 'catalogDate', `"${catalogDate}" is not yyyy-MM-dd`);
  const erasesRaw = scalars.get('erases')!;
  if (erasesRaw !== 'true' && erasesRaw !== 'false') fail(file, 'erases', 'must be true or false');
  const confidence = scalars.get('confidence')!;
  if (!isConfidence(confidence)) fail(file, 'confidence', 'must be high, medium or low');
  const sources = lists.get('sources')!.map((l) => parseSourceLine(l, file));

  // The body: "## Steps" first, numbered 1, 2, 3…; erasing steps last; optional "## Good to know".
  const lines = body.split('\n');
  if (lines[0]?.trim() !== '## Steps') fail(file, 'body', 'must start with "## Steps"');
  let section = '';
  let expected = 1;
  const seen = new Set<string>();
  for (const line of lines) {
    if (line.startsWith('#')) {
      const h = line.trim();
      if (!(BODY_HEADINGS as readonly string[]).includes(h)) fail(file, 'body', `"${h}" is not an allowed heading (${BODY_HEADINGS.join(', ')})`);
      if (seen.has(h)) fail(file, 'body', `"${h}" appears twice`);
      seen.add(h);
      section = h;
      continue;
    }
    if (section === '## Steps' && line.trim()) {
      const m = /^(\d+)\. \S/.exec(line);
      if (!m) fail(file, 'body', `"${line}" in Steps is not a numbered step`);
      if (Number(m[1]) !== expected) fail(file, 'body', `step ${m[1]} should be numbered ${expected}`);
      expected += 1;
    }
  }
  const steps = entrySteps(body);
  if (steps.length === 0) fail(file, 'body', 'has no steps');
  const firstErase = steps.findIndex(isEraseStep);
  if (firstErase >= 0 && steps.slice(firstErase).some((s) => !isEraseStep(s))) fail(file, 'body', 'a step that erases must come after every step that does not');
  if (steps.some((s) => s.includes('⚠') && !isEraseStep(s))) fail(file, 'body', `a warning step must start with "${ERASE_MARK}"`);
  const erases = erasesRaw === 'true';
  if (erases !== firstErase >= 0) fail(file, 'erases', erases ? 'is true but no step starts with the erase warning' : `is false but a step starts with "${ERASE_MARK}"`);

  return {
    id,
    title: scalars.get('title')!,
    platform,
    devices: devices as HelpKey[],
    version: scalars.get('version')!,
    topics: lists.get('topics')!,
    sources,
    catalogDate,
    erases,
    confidence,
    body,
  };
}

type Json = Record<string, unknown>;
const str = (o: Json, k: string, file: string, optional = false): string | undefined => {
  const v = o[k];
  if (v === undefined && optional) return undefined;
  if (typeof v !== 'string' || !v.trim()) fail(file, k, 'must be a non-empty string');
  return v;
};

/** Check one flow file (flows/<id>.json): every field, unique steps, erasing steps last with their warning. */
export function parseFlow(value: unknown, file = '<flow>', entryIds?: ReadonlySet<string>): GuidedFlow {
  if (!value || typeof value !== 'object') fail(file, 'flow', 'is not an object');
  const o = value as Json;
  const allowed = ['id', 'title', 'devices', 'match', 'firstQuestion', 'steps', 'end', 'version', 'confidence', 'decisionGuideId'];
  for (const k of Object.keys(o)) if (!allowed.includes(k)) fail(file, k, 'is not a known field');
  const id = str(o, 'id', file)!;
  if (!ID_RE.test(id)) fail(file, 'id', `"${id}" must be lower-case words joined by hyphens`);
  const title = str(o, 'title', file)!;
  const devices = o.devices;
  if (!Array.isArray(devices) || devices.length === 0 || devices.some((d) => typeof d !== 'string' || !isHelpKey(d))) fail(file, 'devices', 'must be a list of help keys');
  const match = o.match;
  if (!Array.isArray(match) || match.length === 0 || match.some((m) => typeof m !== 'string' || !m.trim())) fail(file, 'match', 'must be a list of words');
  const firstQuestion = str(o, 'firstQuestion', file, true);
  const version = str(o, 'version', file)!;
  const confidence = str(o, 'confidence', file)!;
  if (!isConfidence(confidence)) fail(file, 'confidence', 'must be high, medium or low');

  if (!Array.isArray(o.steps) || o.steps.length === 0) fail(file, 'steps', 'must be a non-empty list');
  const steps: FlowStep[] = [];
  const ids = new Set<string>();
  const texts = new Set<string>();
  for (const [i, s] of (o.steps as unknown[]).entries()) {
    const where = `steps[${i}]`;
    if (!s || typeof s !== 'object') fail(file, where, 'is not an object');
    const so = s as Json;
    for (const k of Object.keys(so)) if (!['id', 'text', 'check', 'erases', 'warning', 'entry', 'sourceUrl', 'quote', 'sectionId'].includes(k)) fail(file, `${where}.${k}`, 'is not a known field');
    const sid = str(so, 'id', `${file} ${where}`)!;
    const text = str(so, 'text', `${file} ${where}`)!;
    const check = str(so, 'check', `${file} ${where}`, true);
    const warning = str(so, 'warning', `${file} ${where}`, true);
    const entry = str(so, 'entry', `${file} ${where}`)!;
    if (typeof so.erases !== 'boolean') fail(file, `${where}.erases`, 'must be true or false');
    if (ids.has(sid)) fail(file, `${where}.id`, `"${sid}" is used twice`);
    if (texts.has(text)) fail(file, `${where}.text`, 'repeats an earlier step');
    ids.add(sid);
    texts.add(text);
    if (so.erases && !warning) fail(file, `${where}.warning`, 'a step that erases must carry its warning');
    if (warning && !/^This erases /.test(warning)) fail(file, `${where}.warning`, 'must start with "This erases "');
    if (!so.erases && warning) fail(file, `${where}.warning`, 'only a step that erases carries a warning');
    if (entryIds && !entryIds.has(entry)) fail(file, `${where}.entry`, `"${entry}" is not a library entry`);
    const sourceUrl = str(so, 'sourceUrl', `${file} ${where}`, true);
    const quote = str(so, 'quote', `${file} ${where}`, true);
    const sectionId = str(so, 'sectionId', `${file} ${where}`, true);
    if (sourceUrl && !/^https:\/\/\S+$/.test(sourceUrl)) fail(file, `${where}.sourceUrl`, 'must be HTTPS');
    if ((sourceUrl || quote || sectionId) && !(sourceUrl && quote && sectionId)) fail(file, `${where}.quote`, 'source URL, section ID and quote must be supplied together');
    steps.push({ id: sid, text, ...(check ? { check } : {}), erases: so.erases as boolean, ...(warning ? { warning } : {}), entry, ...(sourceUrl ? { sourceUrl, quote, sectionId } : {}) });
  }
  const firstErase = steps.findIndex((s) => s.erases);
  if (firstErase >= 0 && steps.slice(firstErase).some((s) => !s.erases)) fail(file, 'steps', 'a step that erases must come after every step that does not');

  const e = o.end as Json | undefined;
  if (!e || typeof e !== 'object') fail(file, 'end', 'is missing');
  const maker = str(e, 'maker', `${file} end`)!;
  if (!isPlatform(maker)) fail(file, 'end.maker', `"${maker}" is not a maker in the catalog`);
  const supportUrl = str(e, 'supportUrl', `${file} end`)!;
  if (!/^https:\/\/\S+$/.test(supportUrl)) fail(file, 'end.supportUrl', 'must be an https link');
  let freeLine: FreeLine | undefined;
  if (e.freeLine !== undefined) {
    const f = e.freeLine as Json;
    freeLine = { name: str(f, 'name', `${file} end.freeLine`)!, phone: str(f, 'phone', `${file} end.freeLine`)!, hours: str(f, 'hours', `${file} end.freeLine`)! };
  }
  return {
    id,
    title,
    devices: devices as HelpKey[],
    match: match as string[],
    ...(firstQuestion ? { firstQuestion } : {}),
    steps,
    end: { maker, supportUrl, ...(freeLine ? { freeLine } : {}) },
    version,
    confidence,
    ...(o.decisionGuideId ? { decisionGuideId: str(o, 'decisionGuideId', file)! } : {}),
  };
}

// ---------------------------------------------------------------------------------------------------------------
// The Wizard's functions.
// ---------------------------------------------------------------------------------------------------------------

const STOP = new Set(
  'a an and are as at be but by can cant could did do does doesnt dont for from get gets got has have he her his how i im in is isnt it its just me my no not of on or our she so that the their them then there they this to too up was wasnt we what when where which who why will with wont you your'.split(' '),
);

/** Lower-case words, apostrophes dropped, stop words left out. */
export function words(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[’']/g, '')
    .split(/[^a-z0-9+]+/)
    .filter((w) => w.length > 1 && !STOP.has(w));
}

/** Words that name a maker (a device name like "iPhone 12" or "Galaxy A15" points to it). */
const MAKER_WORDS: Record<string, Platform> = {
  iphone: 'Apple', ipad: 'Apple', mac: 'Apple', macbook: 'Apple', imac: 'Apple', apple: 'Apple', icloud: 'Apple', homepod: 'Apple', airpods: 'Apple', voiceover: 'Apple',
  android: 'Google', pixel: 'Google', google: 'Google', gmail: 'Google', talkback: 'Google', nest: 'Google',
  samsung: 'Samsung', galaxy: 'Samsung',
  kindle: 'Amazon', amazon: 'Amazon', voiceview: 'Amazon',
  windows: 'Microsoft', microsoft: 'Microsoft', outlook: 'Microsoft', narrator: 'Microsoft', surface: 'Microsoft',
  jaws: 'Vispero', zoomtext: 'Vispero', fusion: 'Vispero',
  nvda: 'NV Access',
  dell: 'Dell', inspiron: 'Dell', latitude: 'Dell',
  hp: 'HP', envy: 'HP', deskjet: 'HP', officejet: 'HP', laserjet: 'HP', pavilion: 'HP',
  lenovo: 'Lenovo', thinkpad: 'Lenovo', ideapad: 'Lenovo',
  epson: 'Epson', ecotank: 'Epson',
  brother: 'Brother',
  canon: 'Canon', pixma: 'Canon',
  spectrum: 'Spectrum', charter: 'Spectrum',
  verizon: 'Verizon', fios: 'Verizon',
  nls: 'NLS', bard: 'NLS', da2: 'NLS', da1: 'NLS', ds1: 'NLS',
  victor: 'HumanWare', humanware: 'HumanWare', braillenote: 'HumanWare', brailliant: 'HumanWare', explore: 'HumanWare',
  index: 'Index', embosser: 'Index',
  viewplus: 'ViewPlus', tiger: 'ViewPlus',
  duxbury: 'Duxbury', dbt: 'Duxbury',
  focus: 'Freedom Scientific', ruby: 'Freedom Scientific', topaz: 'Freedom Scientific', pearl: 'Freedom Scientific',
  braillesense: 'SELVAS BLV',
  orbit: 'Orbit Research',
  roku: 'Roku',
  orcam: 'OrCam',
  envision: 'Envision',
};

export interface LibraryQuery {
  /** The ticket's description or the tech's question. */
  text?: string;
  /** The help kind(s) of the ticket. */
  help?: HelpKey | HelpKey[];
  /** The device as typed ("iPhone 12"). */
  device?: string;
}

const asList = (h: HelpKey | HelpKey[] | undefined): HelpKey[] => (h === undefined ? [] : Array.isArray(h) ? h : [h]);

/** A plain word score: title ×4, topics ×3, the maker named ×5, the help kind ×2, the body ×1 per word. */
export function scoreEntry(entry: LibraryEntry, q: LibraryQuery): number {
  const qWords = [...new Set(words(`${q.text ?? ''} ${q.device ?? ''}`))];
  const help = asList(q.help);
  const title = new Set(words(entry.title));
  const topics = new Set(entry.topics.flatMap(words));
  const body = new Set(words(entry.body));
  const makers = new Set(qWords.map((w) => MAKER_WORDS[w]).filter((p): p is Platform => !!p));
  let wordScore = 0;
  for (const w of qWords) {
    if (title.has(w)) wordScore += 4;
    if (topics.has(w)) wordScore += 3;
    if (body.has(w)) wordScore += 1;
  }
  let score = wordScore;
  if (makers.has(entry.platform)) score += 5;
  if (help.some((h) => entry.devices.includes(h))) score += 2;
  // A help kind or a maker alone is not a match: some word of the question must be in the entry,
  // unless the question has no words at all (then the help kind and the device are all there is).
  if (wordScore === 0 && qWords.some((w) => !MAKER_WORDS[w])) return 0;
  return score;
}

/** The entries that answer a question, best first (ties: higher confidence, then title). No AI. */
export function searchLibrary(lib: Pick<TechLibrary, 'entries'>, q: LibraryQuery, limit = 8): LibraryEntry[] {
  const rank: Record<Confidence, number> = { high: 0, medium: 1, low: 2 };
  return lib.entries
    .map((entry) => ({ entry, score: scoreEntry(entry, q) }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score || rank[a.entry.confidence] - rank[b.entry.confidence] || a.entry.title.localeCompare(b.entry.title))
    .slice(0, limit)
    .map((r) => r.entry);
}

/** The guided flows a description points to, best first (match words ×3, title ×2, help kind ×2, maker ×3). */
export function matchFlows(lib: Pick<TechLibrary, 'flows'>, q: LibraryQuery, limit = 3): GuidedFlow[] {
  const qWords = new Set(words(`${q.text ?? ''} ${q.device ?? ''}`));
  const help = asList(q.help);
  const makers = new Set([...qWords].map((w) => MAKER_WORDS[w]).filter((p): p is Platform => !!p));
  return lib.flows
    .map((flow) => {
      let s = 0;
      for (const m of flow.match) if (words(m).every((w) => qWords.has(w))) s += 3;
      for (const w of words(flow.title)) if (qWords.has(w)) s += 2;
      if (s === 0) return { flow, s };
      if (help.some((h) => flow.devices.includes(h))) s += 2;
      if (makers.has(flow.end.maker)) s += 3;
      return { flow, s };
    })
    .filter((r) => r.s > 0)
    .sort((a, b) => b.s - a.s || a.flow.title.localeCompare(b.flow.title))
    .slice(0, limit)
    .map((r) => r.flow);
}

export type NextStep =
  | { done: false; step: FlowStep; /** steps left after this one */ remaining: number }
  | { done: true; end: FlowEnd };

/**
 * "I already tried that": the next step not yet tried — the safe steps in order, then the ones that erase, in
 * order, each with its warning. Never a step in `tried` (ids). When none are left: the maker's page and free line.
 */
export function nextStep(flow: GuidedFlow, tried: readonly string[]): NextStep {
  const done = new Set(tried);
  const ordered = [...flow.steps.filter((s) => !s.erases), ...flow.steps.filter((s) => s.erases)];
  const left = ordered.filter((s) => !done.has(s.id));
  const step = left[0];
  if (!step) return { done: true, end: flow.end };
  return { done: false, step, remaining: left.length - 1 };
}

/** The civil date six months before `today` (yyyy-MM-dd), day clamped to the month's length. */
export function sixMonthsBefore(today: string): string {
  if (!DATE_RE.test(today)) throw new Error(`staleEntries: today "${today}" is not yyyy-MM-dd`);
  const [y, m, d] = today.split('-').map(Number) as [number, number, number];
  let year = y;
  let month = m - 6;
  if (month < 1) {
    month += 12;
    year -= 1;
  }
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const day = Math.min(d, last);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export interface StaleItem<T> {
  item: T;
  /** null: never checked. */
  lastCheck: LibraryCheck | null;
}

function stale<T extends { id: string }>(items: readonly T[], checks: Readonly<Record<string, LibraryCheck>>, today: string): StaleItem<T>[] {
  const cutoff = sixMonthsBefore(today);
  return items
    .map((item) => ({ item, lastCheck: checks[item.id] ?? null }))
    .filter(({ lastCheck }) => !lastCheck || lastCheck.at.slice(0, 10) < cutoff);
}

/** Entries never checked, or last checked more than six months before `today` (yyyy-MM-dd, America/New_York). */
export function staleEntries(lib: Pick<TechLibrary, 'entries'>, checks: Readonly<Record<string, LibraryCheck>>, today: string): StaleItem<LibraryEntry>[] {
  return stale(lib.entries, checks, today);
}

/** The same for the guided flows (their checks are kept under "flow:<id>"). */
export function staleFlows(lib: Pick<TechLibrary, 'flows'>, checks: Readonly<Record<string, LibraryCheck>>, today: string): StaleItem<GuidedFlow>[] {
  const byId: Record<string, LibraryCheck> = {};
  for (const [k, v] of Object.entries(checks)) if (k.startsWith('flow:')) byId[k.slice(5)] = v;
  return stale(lib.flows, byId, today);
}

/**
 * "Is there free help for this?": the lines for this help kind — the maker's own line first when a maker is named,
 * then the other lines for this kind, then the ones for everything (Be My Eyes, Aira). Scam lines only when asked.
 */
export function freeHelpFor(lib: Pick<TechLibrary, 'freeHelp'>, help: HelpKey, opts: { maker?: Platform; scam?: boolean } = {}): FreeHelpLine[] {
  const lines = lib.freeHelp.lines.filter((l) => (opts.scam ? true : !l.scam));
  const forKind = lines.filter((l) => l.helps !== 'all' && l.helps.includes(help));
  const forAll = lines.filter((l) => l.helps === 'all');
  const own = opts.maker ? forKind.filter((l) => l.maker === opts.maker) : [];
  const rest = forKind.filter((l) => !own.includes(l));
  const scamFirst = opts.scam ? forAll.filter((l) => l.scam) : [];
  return [...scamFirst, ...own, ...rest, ...forAll.filter((l) => !scamFirst.includes(l))];
}

/** 1.8.5 (the Wizard): the makers a text names ("iPhone 12" → Apple), in the order they first appear. */
export function makersIn(text: string): Platform[] {
  const out: Platform[] = [];
  for (const w of words(text)) {
    const p = MAKER_WORDS[w];
    if (p && !out.includes(p)) out.push(p);
  }
  return out;
}
